import { ref } from 'vue';

let fbInstance = null;
let initPromise = null;

export const useFacebookSdk = () => {
    const loading = ref(true);
    const error = ref(null);

    const config = useRuntimeConfig();

    const loadSdk = () => {
        return new Promise((resolve, reject) => {
            if (document.getElementById('facebook-jssdk')) {
                if (window.FB) {
                    resolve(); // Already loaded and available
                } else {
                    // Wait a bit to see if FB becomes available
                    const check = setInterval(() => {
                        if (window.FB) {
                            clearInterval(check);
                            resolve();
                        }
                    }, 50);
                    setTimeout(() => {
                        clearInterval(check);
                        reject(new Error('Facebook SDK script loaded but FB is undefined'));
                    }, 3000);
                }
                return;
            }

            const script = document.createElement('script');
            script.id = 'facebook-jssdk';
            script.src = 'https://connect.facebook.net/en_US/sdk.js';
            script.async = true;
            script.defer = true;
            script.crossOrigin = 'anonymous';
            script.onload = resolve;
            script.onerror = () => reject(new Error('Failed to load Facebook SDK'));
            document.head.appendChild(script);
        });
    };

    const initFb = () => {
        try {
            window.FB.init({
                appId: config.public.facebookAppId,
                xfbml: true,
                version: 'v23.0'
            });
            fbInstance = window.FB;
        } catch (e) {
            throw new Error('FB.init failed: ' + e.message);
        }
    };

    const getSdk = async () => {
        if (typeof window === 'undefined') {
            error.value = 'Not running in browser';
            loading.value = false;
            return null;
        }

        if (fbInstance) {
            loading.value = false;
            return fbInstance;
        }

        if (!initPromise) {
            initPromise = new Promise(async (resolve, reject) => {
                try {
                    // Always set fbAsyncInit BEFORE loading script
                    window.fbAsyncInit = function () {
                        try {
                            initFb();
                            loading.value = false;
                            resolve(window.FB);
                        } catch (e) {
                            error.value = e.message;
                            loading.value = false;
                            reject(e);
                        }
                    };

                    await loadSdk();

                    // If FB is already defined (script was cached), call init immediately
                    if (window.FB) {
                        initFb();
                        loading.value = false;
                        resolve(window.FB);
                    }
                    // else fbAsyncInit will be triggered by Facebook when it loads
                } catch (err) {
                    error.value = err.message;
                    loading.value = false;
                    reject(err);
                }
            });
        }

        return initPromise;
    };

    return {
        getSdk,
        loading,
        error
    };
};